import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';

const SignatureCapture = ({ 
  name, 
  label, 
  value, 
  onChange, 
  width = 400, 
  height = 150, 
  disabled = false,
  required = false 
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = width;
    canvas.height = height;
    
    // Set up canvas styling
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Load existing signature if provided
    if (value && typeof value === 'string' && value.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        setHasSignature(true);
        setIsEmpty(false);
      };
      img.src = value;
    }
  }, [value, width, height]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    
    if (e.touches && e.touches[0]) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    setIsDrawing(true);
    setIsEmpty(false);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing || disabled) return;
    
    e.preventDefault();
    setIsDrawing(false);
    
    // Convert canvas to base64 and trigger onChange
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    setHasSignature(true);
    
    if (onChange) {
      onChange(name, dataURL);
    }
  };

  const clearSignature = () => {
    if (disabled) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    setHasSignature(false);
    setIsEmpty(true);
    
    if (onChange) {
      onChange(name, '');
    }
  };

  const isSignatureValid = () => {
    return !required || (hasSignature && !isEmpty);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1, 
            fontWeight: 500,
            color: required && !isSignatureValid() ? 'error.main' : 'text.primary'
          }}
        >
          {label} {required && '*'}
        </Typography>
      )}
      
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 1, 
          display: 'inline-block',
          borderColor: required && !isSignatureValid() ? 'error.main' : 'grey.300'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            cursor: disabled ? 'not-allowed' : 'crosshair',
            touchAction: 'none',
            display: 'block'
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearSignature}
            disabled={disabled || isEmpty}
          >
            Clear
          </Button>
          
          {hasSignature && !isEmpty && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
              <CheckIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">Signature captured</Typography>
            </Box>
          )}
          
          {disabled && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <Typography variant="caption">Read-only</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      
      {required && !isSignatureValid() && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          Signature is required
        </Typography>
      )}
      
      {hasSignature && !isEmpty && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Signature will be stored as part of the record
        </Typography>
      )}
    </Box>
  );
};

export default SignatureCapture;